// Narration and voice memos. Must match the writing-audio bucket's
// allowed_mime_types and 25 MB limit (writings_v2 migration).

export const ALLOWED_AUDIO_TYPES = [
  'audio/webm',
  'audio/ogg',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/aac',
  'audio/x-m4a',
]
export const MAX_AUDIO_BYTES = 25 * 1024 * 1024

/** MediaRecorder reports e.g. "audio/webm;codecs=opus" — the bucket wants the bare type. */
export function baseAudioType(type: string): string {
  return type.split(';')[0].trim().toLowerCase()
}

export function assertUploadableAudio(file: Blob): void {
  if (!ALLOWED_AUDIO_TYPES.includes(baseAudioType(file.type))) {
    throw new Error('Audio must be WebM, Ogg, MP3, M4A/AAC, or WAV.')
  }
  if (file.size > MAX_AUDIO_BYTES) throw new Error('Audio must be under 25 MB.')
}

export function audioExtension(type: string): string {
  switch (baseAudioType(type)) {
    case 'audio/webm':
      return 'webm'
    case 'audio/ogg':
      return 'ogg'
    case 'audio/mpeg':
      return 'mp3'
    case 'audio/wav':
    case 'audio/x-wav':
      return 'wav'
    case 'audio/aac':
      return 'aac'
    default:
      return 'm4a'
  }
}

/** What recording or picking a file produces, already uploaded. */
export interface CapturedAudio {
  src: string
  /** Seconds. */
  duration: number
  /** ~40 normalised peaks (0..1) for a static waveform. */
  peaks: number[]
  recordedAt: string
}

/** "6:12" */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Longest take the capture sheet allows for each use (its `maxSeconds`). */
export const NARRATION_MAX_SECONDS = 20 * 60
export const VOICE_MEMO_MAX_SECONDS = 3 * 60

/** "3 minutes", "20 minutes", "45 seconds" — for limits in messages. */
export function formatLimit(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} seconds`
  const m = Math.round(seconds / 60)
  return m === 1 ? '1 minute' : `${m} minutes`
}

// Browsers label audio loosely: "audio/mp3", "audio/x-aac", or nothing at all
// for .m4a on some systems. Map those to what the bucket accepts.
const TYPE_ALIASES: Record<string, string> = {
  'audio/mp3': 'audio/mpeg',
  'audio/mpeg3': 'audio/mpeg',
  'audio/x-mpeg': 'audio/mpeg',
  'audio/x-mp3': 'audio/mpeg',
  'audio/wave': 'audio/wav',
  'audio/vnd.wave': 'audio/wav',
  'audio/x-aac': 'audio/aac',
  'audio/m4a': 'audio/x-m4a',
  'audio/opus': 'audio/ogg',
}
const EXTENSION_TYPES: Record<string, string> = {
  webm: 'audio/webm',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  mp3: 'audio/mpeg',
  m4a: 'audio/x-m4a',
  mp4: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
}

/** The bucket's name for a file's audio type, else its extension's; '' when it isn't audio we take. */
export function canonicalAudioType(type: string, name = ''): string {
  const base = baseAudioType(type)
  if (ALLOWED_AUDIO_TYPES.includes(base)) return base
  const alias = TYPE_ALIASES[base]
  if (alias) return alias
  const ext = name.includes('.') ? (name.split('.').pop() ?? '').toLowerCase() : ''
  return EXTENSION_TYPES[ext] ?? ''
}

/** The picked file relabelled with its canonical type when the browser's label is off; otherwise itself. */
export function asUploadableAudio(file: Blob & { name?: string }): Blob {
  const type = canonicalAudioType(file.type, file.name ?? '')
  if (!type || type === baseAudioType(file.type)) return file
  return new Blob([file], { type })
}
